import React, { useState, useEffect, useRef } from 'react';
import { Box, Paper, Typography, Button, Alert, Tabs, Tab, CircularProgress } from '@mui/material';
import { PlayArrow, Stop } from '@mui/icons-material';
import Editor from '@monaco-editor/react';

function TypeScriptWorkspace({ question, onCodeChange, initialCode = '' }) {
  const [code, setCode] = useState(initialCode || '// Write your TypeScript code here\n');
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const editorRef = useRef(null);

  useEffect(() => {
    if (onCodeChange) {
      onCodeChange(code);
    }
  }, [code, onCodeChange]);

  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;

    // Configure TypeScript compiler options
    monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
      target: monaco.languages.typescript.ScriptTarget.ES2020,
      allowNonTsExtensions: true,
      moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
      module: monaco.languages.typescript.ModuleKind.CommonJS,
      noEmit: true,
      esModuleInterop: true,
      jsx: monaco.languages.typescript.JsxEmit.React,
      allowJs: true,
      typeRoots: ['node_modules/@types'],
    });

    // Add Node.js type definitions
    monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
      noSemanticValidation: false,
      noSyntaxValidation: false,
    });
  };

  const runCode = async () => {
    setIsRunning(true);
    setOutput('Compiling TypeScript...\n');
    setActiveTab(1); // Switch to output tab

    try {
      // In a real implementation, this would send code to backend for execution
      // For now, we'll use a simple eval (not recommended for production)
      const ts = window.ts;

      if (ts) {
        // Transpile TypeScript to JavaScript
        const result = ts.transpile(code, {
          target: ts.ScriptTarget.ES2020,
          module: ts.ModuleKind.CommonJS,
        });

        setOutput('TypeScript compiled successfully!\n\n--- Compiled JavaScript ---\n' + result);

        // Optionally execute the compiled code
        try {
          // Capture console output
          const logs = [];
          const originalLog = console.log;
          console.log = (...args) => {
            logs.push(args.join(' '));
            originalLog(...args);
          };

          // Execute code
          eval(result);

          console.log = originalLog;

          if (logs.length > 0) {
            setOutput(prev => prev + '\n\n--- Output ---\n' + logs.join('\n'));
          }
        } catch (execError) {
          setOutput(prev => prev + '\n\n--- Runtime Error ---\n' + execError.message);
        }
      } else {
        // Fallback: send to backend
        const response = await fetch('/api/interviews/execute-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            code,
            language: 'typescript',
            stdin: '',
          }),
        });

        const data = await response.json();

        if (data.output) {
          setOutput(data.output);
        } else if (data.error) {
          setOutput('Error:\n' + data.error);
        }
      }
    } catch (error) {
      setOutput('Execution error: ' + error.message);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Question Display */}
      {question && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            {question.name || 'TypeScript Challenge'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
            {question.task || question.question || question.text}
          </Typography>
        </Paper>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="TypeScript Editor" />
        <Tab label="Output" />
      </Tabs>

      {/* Editor Tab */}
      {activeTab === 0 && (
        <Box sx={{ flexGrow: 1, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
          <Editor
            height="100%"
            defaultLanguage="typescript"
            value={code}
            onChange={(value) => setCode(value || '')}
            onMount={handleEditorDidMount}
            theme="vs-dark"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              roundedSelection: false,
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
            }}
          />
        </Box>
      )}

      {/* Output Tab */}
      {activeTab === 1 && (
        <Paper
          variant="outlined"
          sx={{
            flexGrow: 1,
            p: 2,
            bgcolor: '#1e1e1e',
            color: '#d4d4d4',
            fontFamily: 'monospace',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          {output || 'Click "Run Code" to see output here...'}
        </Paper>
      )}

      {/* Run Button */}
      <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          startIcon={isRunning ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
          onClick={runCode}
          disabled={isRunning}
        >
          {isRunning ? 'Running...' : 'Run Code'}
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <strong>TypeScript Workspace</strong> - Write TypeScript code with full type checking support.
        Code will be transpiled and executed.
      </Alert>
    </Box>
  );
}

export default TypeScriptWorkspace;
